import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createGroupChatGroupsPost,
  getGroupChatGroupsGroupIdGet,
  listGroupsChatGroupsGet,
  listPickableRolesChatGroupsRolesGet,
  listPickableSourcesChatGroupsSourcesGet,
  updateGroupChatGroupsGroupIdPut,
} from '../../services/api/sdk.gen';
import type {
  GroupDetail,
  GroupRow,
  GroupWrite,
  PickableRole,
  PickableSources,
} from '../../services/api/types.gen';

export const groupKeys = {
  all: ['groups'] as const,
  list: (q: string, includeArchived: boolean) =>
    [...groupKeys.all, 'list', q, includeArchived] as const,
  detail: (id: string) => [...groupKeys.all, 'detail', id] as const,
  roles: () => [...groupKeys.all, 'roles'] as const,
  sources: () => [...groupKeys.all, 'sources'] as const,
};

/** The reconcile writes a new role's holders about two seconds after commit,
 *  so a save that adds a role refetches the groups once, three seconds later.
 *  The list carries member_count, so the refetch has to cover it too. */
export const ROLE_SYNC_REFETCH_MS = 3000;
export const groupMutationKeys = {
  update: ['groups', 'update'] as const,
};

type UpdateVariables = { id: string; body: GroupWrite };

// Role and source entries are resolved by the async group reconcile, so a save
// that adds or removes one needs the delayed refetch.
function hasResolvedEntry(body: GroupWrite): boolean {
  return (body.members ?? []).some(
    entry => entry.kind === 'role' || entry.kind === 'source'
  );
}

export function useGroups(q: string, includeArchived: boolean) {
  return useQuery({
    queryKey: groupKeys.list(q, includeArchived),
    queryFn: async (): Promise<GroupRow[]> => {
      const { data } = await listGroupsChatGroupsGet({
        query: { q: q || undefined, include_archived: includeArchived },
        throwOnError: true,
      });
      return data ?? [];
    },
  });
}

export function useGroupDetail(groupId: string | null) {
  return useQuery({
    enabled: groupId !== null,
    queryKey: groupKeys.detail(groupId ?? ''),
    queryFn: async (): Promise<GroupDetail> => {
      const { data } = await getGroupChatGroupsGroupIdGet({
        path: { group_id: groupId as string },
        throwOnError: true,
      });
      return data as GroupDetail;
    },
  });
}

export function usePickableRoles() {
  return useQuery({
    queryKey: groupKeys.roles(),
    queryFn: async (): Promise<PickableRole[]> => {
      const { data } = await listPickableRolesChatGroupsRolesGet({
        throwOnError: true,
      });
      return data ?? [];
    },
  });
}

export function usePickableSources() {
  return useQuery({
    queryKey: groupKeys.sources(),
    queryFn: async (): Promise<PickableSources> => {
      const { data } = await listPickableSourcesChatGroupsSourcesGet({
        throwOnError: true,
      });
      return data as PickableSources;
    },
  });
}

export function useCreateGroup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: GroupWrite) => {
      const { data } = await createGroupChatGroupsPost({
        body,
        throwOnError: true,
      });
      return data as GroupDetail;
    },
    // A create with a role entry needs the same 3 s refetch an update does, or
    // the new group's editor and row show the role with no holders until a
    // reload.
    onSuccess: (_data, body) => {
      client.invalidateQueries({ queryKey: groupKeys.all });
      if (hasResolvedEntry(body)) {
        window.setTimeout(() => {
          client.invalidateQueries({ queryKey: groupKeys.all });
        }, ROLE_SYNC_REFETCH_MS);
      }
    },
  });
}

export function useUpdateGroup() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: groupMutationKeys.update,
    mutationFn: async (variables: UpdateVariables) => {
      const { data } = await updateGroupChatGroupsGroupIdPut({
        path: { group_id: variables.id },
        body: variables.body,
        throwOnError: true,
      });
      return data as GroupDetail;
    },
    onSuccess: (_data, variables) => {
      // Read the cached detail before the invalidation below replaces it: a
      // save that removes the last role must also wait for the reconcile to
      // drop that role's holders, or the headline and the row's member count
      // keep counting them.
      const previous = client.getQueryData<GroupDetail>(
        groupKeys.detail(variables.id)
      );
      const hadResolvedEntry = (previous?.entries ?? []).some(
        entry => entry.kind === 'role' || entry.kind === 'source'
      );
      client.invalidateQueries({ queryKey: groupKeys.all });
      if (hadResolvedEntry || hasResolvedEntry(variables.body)) {
        window.setTimeout(() => {
          client.invalidateQueries({ queryKey: groupKeys.all });
        }, ROLE_SYNC_REFETCH_MS);
      }
    },
  });
}
